import { useState, useEffect, useRef } from "react"
import { ChevronDown, Upload } from "lucide-react"
import { useParams, useNavigate } from "react-router-dom"
import JobListingService from "../services/JobListingService"
import ProfileSidebar from "../components/ProfileSideBar"
import OrgScheduleInterviewForm from "../components/OrgScheduleInterviewForm"
import { useUser } from "../contexts/UserContext"
import OrganizationService from "../services/OrganizationService"

import toast from "react-hot-toast"
import ShareJobPostModal from "../components/ShareJobPostModal"
import ReEvaluationManager from "../components/ReEvaluationManager"
import EditIdealCandidateModal from "../components/job-details/EditIdealCandidateModal"
import CandidateListContent from "../components/CandidateListContent"
import Leads from "../components/Leads"
import JobIdInterviews from "../components/JobIdInterviews"

// ── Page shimmer ──────────────────────────────────────────────────────────────
const PageShimmer = () => (
  <div className="min-h-screen bg-zinc-50">
    <div className="flex">
      <div className="border-r border-zinc-200 h-full bg-white overflow-y-auto min-h-screen flex-shrink-0">
        <ProfileSidebar activeTab="jobListings" setActiveTab={() => {}} />
      </div>

      <div className="flex-1 ml-60 flex flex-col h-screen overflow-hidden">
        {/* Top nav — Back · Leads · Applicant · Interviews */}
        <div
          className="bg-white border-b border-zinc-200 px-8 flex items-center justify-between flex-shrink-0"
          style={{ height: 48 }}
        >
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 bg-zinc-100 animate-pulse"
                style={{ borderRadius: 0 }}
              />
              <div
                className="h-3 w-8 bg-zinc-100 animate-pulse"
                style={{ borderRadius: 0 }}
              />
            </div>
            {[72, 80, 88].map((w, i) => (
              <div
                key={i}
                className="flex flex-col items-center justify-center h-full gap-1"
                style={{ paddingTop: 14, paddingBottom: 0 }}
              >
                <div
                  className="h-3 bg-zinc-100 animate-pulse"
                  style={{ width: w, borderRadius: 0 }}
                />
                {i === 1 && (
                  <div
                    className="h-0.5 bg-zinc-900 mt-2"
                    style={{ width: w, borderRadius: 0 }}
                  />
                )}
              </div>
            ))}
          </div>
          <div
            className="w-5 h-5 bg-zinc-100 animate-pulse"
            style={{ borderRadius: 0 }}
          />
        </div>

        {/* Toolbar — title · New Applicant · Archived · Search · Sort */}
        <div className="bg-white px-8 py-4 flex items-center justify-between border-b border-zinc-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="h-3 w-20 bg-zinc-100 animate-pulse"
              style={{ borderRadius: 0 }}
            />
            <div
              className="h-5 w-8 bg-zinc-50 border border-zinc-100 animate-pulse"
              style={{ borderRadius: 0 }}
            />
          </div>
          <div className="flex items-center gap-2">
            <div
              className="h-9 w-32 bg-zinc-900 animate-pulse"
              style={{ borderRadius: 0 }}
            />
            <div
              className="h-9 w-24 bg-white border border-zinc-200 animate-pulse"
              style={{ borderRadius: 0 }}
            />
            <div
              className="h-9 w-56 bg-white border border-zinc-200 animate-pulse"
              style={{ borderRadius: 0 }}
            />
            <div
              className="h-9 w-32 bg-white border border-zinc-200 animate-pulse"
              style={{ borderRadius: 0 }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="bg-white overflow-hidden">
              {/* Table header */}
              <div
                className="grid items-center px-6 py-3 border-b border-zinc-200"
                style={{
                  gridTemplateColumns: "2fr 1fr 1fr 1fr 0.9fr 0.6fr",
                  backgroundColor: "rgb(250,249,245)",
                }}
              >
                {[38, 28, 42, 30, 36].map((w, i) => (
                  <div
                    key={i}
                    className="h-2.5 bg-zinc-200 animate-pulse"
                    style={{ width: `${w}%`, borderRadius: 0 }}
                  />
                ))}
                <div />
              </div>

              {/* Rows */}
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className={`grid items-center px-6 py-4 ${i !== 11 ? "border-b border-zinc-100" : ""}`}
                  style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr 0.9fr 0.6fr" }}
                >
                  {/* Candidate */}
                  <div className="flex items-center gap-3 pr-6">
                    <div
                      className="w-8 h-8 flex-shrink-0 bg-zinc-100 animate-pulse"
                      style={{ borderRadius: 0 }}
                    />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div
                        className="h-3.5 bg-zinc-100 animate-pulse"
                        style={{ width: "58%", borderRadius: 0 }}
                      />
                      <div
                        className="h-2.5 bg-zinc-50 animate-pulse"
                        style={{ width: "42%", borderRadius: 0 }}
                      />
                    </div>
                  </div>
                  {/* Match */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-zinc-100 animate-pulse flex-shrink-0" />
                      <div
                        className="h-2.5 bg-zinc-100 animate-pulse"
                        style={{ width: "65%", borderRadius: 0 }}
                      />
                    </div>
                    <div
                      className="h-3.5 bg-zinc-100 animate-pulse"
                      style={{ width: "28%", borderRadius: 0 }}
                    />
                  </div>
                  {/* Experience */}
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-3 h-3 bg-zinc-100 animate-pulse flex-shrink-0"
                      style={{ borderRadius: 0 }}
                    />
                    <div
                      className="h-3 bg-zinc-100 animate-pulse"
                      style={{ width: "45%", borderRadius: 0 }}
                    />
                  </div>
                  {/* Notice */}
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-3 h-3 bg-zinc-100 animate-pulse flex-shrink-0"
                      style={{ borderRadius: 0 }}
                    />
                    <div
                      className="h-3 bg-zinc-100 animate-pulse"
                      style={{ width: "55%", borderRadius: 0 }}
                    />
                  </div>
                  {/* Applied */}
                  <div
                    className="h-3 bg-zinc-100 animate-pulse"
                    style={{ width: "70%", borderRadius: 0 }}
                  />
                  {/* View */}
                  <div className="flex justify-end">
                    <div
                      className="h-7 w-14 bg-zinc-50 border border-zinc-100 animate-pulse"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)

// ── Main page ─────────────────────────────────────────────────────────────────
const JobListingDetailsPage = () => {
  const { orgId, jobId } = useParams()
  const navigate = useNavigate()
  const { userData, organizationId, isInOrganization, organizationData } =
    useUser()

  // ── Data state ──
  const [jobListing, setJobListing] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // ── UI state ──
  const [activeTab, setActiveTab] = useState("Applicant")
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(true)

  const [evaluationProgress, setEvaluationProgress] = useState(null)

  // ── Modal / form state ──
  const [showScheduleForm, setShowScheduleForm] = useState(false)
  const [selectedCandidateForScheduling, setSelectedCandidateForScheduling] =
    useState(null)
  const [interviewersData, setInterviewersData] = useState([])
  const [showShareDropdown, setShowShareDropdown] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState(null)
  const [showEditProfileModal, setShowEditProfileModal] = useState(false)
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [rejectingCandidateId, setRejectingCandidateId] = useState(null)

  // ── Refs ──
  const shareDropdownRef = useRef(null)

  // ── Fetch job details ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId || !jobId) return
    const fetch = async () => {
      try {
        setLoading(true)
        const response = await JobListingService.getJobListingDetails(
          orgId,
          jobId,
        )
        setJobListing(response.data)
      } catch (err) {
        console.error("Error fetching job details:", err)
        setError("Failed to load job details. Please try again later.")
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [orgId, jobId])

  // ── Close share dropdown on outside click ─────────────────────────────────
  useEffect(() => {
    if (!showShareDropdown) return
    const handler = (e) => {
      if (!shareDropdownRef.current?.contains(e.target))
        setShowShareDropdown(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [showShareDropdown])

  // ── Derived data ───────────────────────────────────────────────────────────
  const candidates = jobListing?.applicants || []

  const handleStageChange = (applicantId, newStatus) => {
    setJobListing((prev) => ({
      ...prev,
      applicants: prev.applicants.map((a) =>
        a.applicantId === applicantId
          ? { ...a, applicationStatus: newStatus }
          : a,
      ),
    }))
  }

  const handleRejectCandidate = async (candidate) => {
    if (!candidate?.applicantId || rejectingCandidateId) return
    setRejectingCandidateId(candidate.applicantId)
    try {
      const success = await JobListingService.updateApplicantStatus(
        orgId,
        jobId,
        candidate.applicantId,
        "rejected",
      )
      if (!success) throw new Error("Failed to update applicant status")
      setJobListing((prev) => ({
        ...prev,
        applicants: prev.applicants.map((a) =>
          a.applicantId === candidate.applicantId
            ? { ...a, status: "rejected" }
            : a,
        ),
      }))
    } catch (err) {
      console.error("Error rejecting candidate:", err)
      alert("Failed to reject candidate. Please try again.")
    } finally {
      setRejectingCandidateId(null)
    }
  }

  const fetchInterviewers = async () => {
    if (!isInOrganization || !organizationId) return
    try {
      const orgData = await OrganizationService.getOrganization(organizationId)
      setInterviewersData(
        orgData?.members?.filter(
          (m) => m.role === "interviewer" || m.role === "admin",
        ) ?? [],
      )
    } catch (err) {
      console.error("Error fetching interviewers:", err)
      setInterviewersData([])
    }
  }

  const handleScheduleInterview = async (candidate) => {
    setSelectedCandidateForScheduling(candidate)
    setShowScheduleForm(true)
    await fetchInterviewers()
  }

  const handleScheduleComplete = () => {
    // Optimistically mark as scheduled
    setJobListing((prev) => ({
      ...prev,
      applicants: prev.applicants.map((a) =>
        a.applicantId === selectedCandidateForScheduling?.applicantId
          ? { ...a, applicationStatus: "scheduled" }
          : a,
      ),
    }))
    JobListingService.updateApplicantStatus(
      orgId,
      jobId,
      selectedCandidateForScheduling.applicantId,
      "scheduled",
    ).catch(console.error)
    setShowScheduleForm(false)
    setSelectedCandidateForScheduling(null)
  }

  const handleUpdateIdealCandidateProfile = async (newProfile) => {
    if (!newProfile.trim()) {
      toast.error("Profile cannot be empty")
      return
    }
    setIsUpdatingProfile(true)
    try {
      const success = await JobListingService.updateIdealCandidateProfile(
        orgId,
        jobId,
        newProfile,
      )
      if (!success) throw new Error("Failed to update profile")
      toast.success("Ideal candidate profile updated successfully!", {
        duration: 2000,
        position: "top-center",
      })
      setShowEditProfileModal(false)
      window.location.reload()
    } catch (err) {
      console.error("Error updating profile:", err)
      toast.error("Failed to update profile. Please try again.", {
        duration: 3000,
        position: "top-center",
      })
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) return <PageShimmer />

  if (error)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    )

  if (!jobListing)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Job listing not found</p>
      </div>
    )

  // ── Tab content ────────────────────────────────────────────────────────────
  const renderTabContent = () => {
    if (activeTab === "Applicant") {
      return (
        <div className="h-full overflow-hidden">
          <CandidateListContent
            jobListing={jobListing}
            rejectingCandidateId={rejectingCandidateId}
            onRejectCandidate={handleRejectCandidate}
            onScheduleInterview={handleScheduleInterview}
            evaluationProgress={evaluationProgress}
            onStageChange={handleStageChange}
          />
        </div>
      )
    } else if (activeTab === "Leads") {
      return (
        <div className="h-full overflow-hidden">
          <Leads jobId={jobId} orgId={orgId} jobListing={jobListing} onScheduleInterview={handleScheduleInterview} />
        </div>
      )
    } else if (activeTab === "Interviews") {
      return (
        <div className="h-full overflow-hidden">
          <JobIdInterviews
            jobListing={jobListing}
            rejectingCandidateId={rejectingCandidateId}
            onRejectCandidate={handleRejectCandidate}
            onSchedule={handleScheduleInterview}
          />
        </div>
      )
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Profile sidebar */}
        <div className="border-gray-200 h-full bg-white overflow-y-auto min-h-screen">
          <ProfileSidebar activeTab="jobListings" setActiveTab={() => {}} />
        </div>

        {/* Main content */}
        <div className="flex-1 ml-60 overflow-y-auto">
          <div
            className="h-screen bg-gray-50 flex flex-col"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');`}</style>

            {/* ── Header ── */}
            <div className="bg-white border-b border-zinc-200 px-6 py-0 flex items-stretch justify-between flex-shrink-0">
              {/* Tabs */}
              <div className="flex items-stretch">
                {/* Back button */}
                <button
                  onClick={() => navigate("/jobs")}
                  className="relative px-3 py-3.5 text-zinc-400 hover:text-zinc-700 border-b-2 border-transparent transition-all flex items-center gap-1.5"
                  style={{ borderRadius: 0 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 12H5M12 5l-7 7 7 7" />
                  </svg>
                  <span className="text-[11px] font-bold uppercase tracking-widest">
                    Back
                  </span>
                </button>

                {["Leads", "Applicant", "Interviews"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab)
                      if (tab === "Overview") setIsSidebarMinimized(true)
                    }}
                    className={`relative px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest transition-all border-b-2 ${
                      activeTab === tab
                        ? "text-zinc-900 border-zinc-900"
                        : "text-zinc-400 border-transparent hover:text-zinc-700 hover:border-zinc-300"
                    }`}
                    style={{ borderRadius: 0 }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Right actions */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 0",
                }}
              >
                {/* Share Job dropdown */}
                <div style={{ position: "relative" }} ref={shareDropdownRef}>
                  <button
                    onClick={() => setShowShareDropdown((v) => !v)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      height: 34,
                      padding: "0 12px",
                      borderRadius: 8,
                      border: "0.5px solid #e5e7eb",
                      background: "#fff",
                      color: "#374151",
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "'DM Sans', -apple-system, sans-serif",
                      cursor: "pointer",
                      outline: "none",
                      transition: "border-color 0.12s, color 0.12s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#9ca3af"
                      e.currentTarget.style.color = "#111827"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb"
                      e.currentTarget.style.color = "#374151"
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    Share Job
                    <ChevronDown
                      size={11}
                      strokeWidth={2}
                      color="#6b7280"
                      style={{
                        transform: showShareDropdown
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.15s",
                      }}
                    />
                  </button>

                  {showShareDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        right: 0,
                        background: "#fff",
                        border: "0.5px solid #e5e7eb",
                        borderRadius: 10,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                        zIndex: 500,
                        minWidth: 200,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 14px 6px",
                          borderBottom: "0.5px solid #f3f4f6",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            color: "#9ca3af",
                            fontFamily: "'IBM Plex Mono', monospace",
                          }}
                        >
                          Share this job on
                        </p>
                      </div>
                      {[
                        {
                          id: "linkedin",
                          label: "LinkedIn",
                          color: "#0A66C2",
                          letter: "in",
                        },
                        {
                          id: "naukri",
                          label: "Naukri.com",
                          color: "#7c3aed",
                          letter: "N",
                        },
                        {
                          id: "indeed",
                          label: "Indeed",
                          color: "#1d4ed8",
                          letter: "i",
                        },
                        {
                          id: "glassdoor",
                          label: "Glassdoor",
                          color: "#16a34a",
                          letter: "G",
                        },
                      ].map((p) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setSelectedPlatform(p.id)
                            setModalOpen(true)
                            setShowShareDropdown(false)
                          }}
                          style={{
                            display: "flex",
                            width: "100%",
                            alignItems: "center",
                            gap: 10,
                            padding: "9px 14px",
                            border: "none",
                            borderBottom: "0.5px solid #f9fafb",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#374151",
                            fontFamily: "'DM Sans', sans-serif",
                            transition: "background 0.1s",
                            outline: "none",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#fafaf9"
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent"
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              background: p.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                color: "#fff",
                                fontSize: 9,
                                fontWeight: 700,
                              }}
                            >
                              {p.letter}
                            </span>
                          </div>
                          Post on {p.label}
                        </button>
                      ))}
                      <div style={{ borderTop: "0.5px solid #f3f4f6" }} />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `https://resolut.co.in/jobs/${jobId}`,
                          )
                          setShowShareDropdown(false)
                          toast.success("Link copied to clipboard!", {
                            duration: 2000,
                            position: "top-center",
                          })
                        }}
                        style={{
                          display: "flex",
                          width: "100%",
                          alignItems: "center",
                          gap: 10,
                          padding: "9px 14px",
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#374151",
                          fontFamily: "'DM Sans', sans-serif",
                          transition: "background 0.1s",
                          outline: "none",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#fafaf9"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent"
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          fill="none"
                          stroke="#9ca3af"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                        Copy Job Link
                      </button>
                    </div>
                  )}
                </div>

                {/* Export */}
                <button
                  onClick={() =>
                    toast.success("Feature Under Development!", {
                      duration: 2000,
                      position: "top-center",
                    })
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    height: 34,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "0.5px solid #e5e7eb",
                    background: "#fff",
                    color: "#374151",
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "'DM Sans', -apple-system, sans-serif",
                    cursor: "pointer",
                    outline: "none",
                    transition: "border-color 0.12s, color 0.12s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#9ca3af"
                    e.currentTarget.style.color = "#111827"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e7eb"
                    e.currentTarget.style.color = "#374151"
                  }}
                >
                  <Upload size={12} strokeWidth={2} color="#9ca3af" />
                  Export
                </button>

                {/* ReEvaluation — rightmost */}
                {activeTab === "Applicant" && (
                  <ReEvaluationManager
                    orgId={orgId}
                    jobId={jobId}
                    totalCandidates={candidates.length}
                    onProgressUpdate={setEvaluationProgress}
                  />
                )}
              </div>
            </div>

            {/* ── Tab content ── */}
            <div className="flex-1 overflow-hidden">{renderTabContent()}</div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showScheduleForm && selectedCandidateForScheduling && (
        <OrgScheduleInterviewForm
          isOpen={showScheduleForm}
          onClose={() => {
            setShowScheduleForm(false)
            setSelectedCandidateForScheduling(null)
          }}
          onScheduleComplete={handleScheduleComplete}
          interviewType="schedule"
          organizationData={{
            id: organizationId,
            name: userData?.organizationName,
          }}
          interviewersData={interviewersData}
          preselectedJobListing={{
            id: jobListing.id,
            role: jobListing.role,
            department: jobListing.department,
            roundId:
              jobListing.interview_structure?.rounds?.[0]?.id || "round-1",
          }}
          candidateInfo={{
            applicantId: selectedCandidateForScheduling.applicantId,
            name: selectedCandidateForScheduling.applicantName,
            email:
              selectedCandidateForScheduling.applicantEmail ||
              selectedCandidateForScheduling.personal_information?.email ||
              "ak338952@gmail.com",
          }}
        />
      )}

      {modalOpen && (
        <ShareJobPostModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          jobListing={jobListing}
          orgId={orgId}
          jobId={jobId}
          platform={selectedPlatform}
        />
      )}

      {showEditProfileModal && (
        <EditIdealCandidateModal
          isOpen={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          currentProfile={
            jobListing?.candidateProfile?.idealCandidateProfile || ""
          }
          onSave={handleUpdateIdealCandidateProfile}
          isLoading={isUpdatingProfile}
        />
      )}
    </div>
  )
}

export default JobListingDetailsPage
